import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../redux/hooks";
import { UPDATE_COMPANY_LOGO, UPDATE_USER_PROFILE } from "../redux/action/ActionType";
import { DASHBOARD_ROUTE, SIGNIN_ROUTE } from "../constants/RoutePaths";
import { GET_TEAMDETAILS_BY_ID } from "../common/url";
import API from "../constants/API";
import { authHeader } from "../common/mainfunction";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFeatureFlags } from "../context/FeatureFlagContext";
import { usePermissions } from "../context/PermissionContext";

const SSOCallback = () => {
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { refreshFlags } = useFeatureFlags();
  const { refreshPermissions } = usePermissions();

  useEffect(() => {
    const processCallback = async () => {
      try {
        const token = searchParams.get("token");
        const encryptedData = searchParams.get("data");

        if (!token || !encryptedData) {
          setError("Invalid SSO callback. Missing authentication data.");
          setTimeout(() => navigation(SIGNIN_ROUTE), 3000);
          return;
        }

        // Parse the login data (plain JSON from backend)
        const parsed = JSON.parse(encryptedData);

        if (!parsed || parsed.status !== "Success" || !parsed.data) {
          setError("Authentication failed. Invalid response data.");
          setTimeout(() => navigation(SIGNIN_ROUTE), 3000);
          return;
        }

        const data = parsed.data;
        const invoiceCount = data.invoiceCount || 0;
        const exceptionRoleId = data.tblExceptionHandlerRoleId || 0;

        // Store all localStorage keys (same as Signin.js)
        localStorage.setItem("User_ID", data.userId);
        localStorage.setItem("AUTH_TOKEN", token);
        localStorage.setItem("Team_ID", data.teamId);
        localStorage.setItem("EMAIL", data.email);
        localStorage.setItem("SENDER_EMAIL", data.invoiceSenderEmail);
        localStorage.setItem("USER_ROLE", data.userRole);
        localStorage.setItem("PROFILE_PIC", data.profileLogo);
        localStorage.setItem("APPROVAL_AMOUNT", data.approvalAmountTo);
        localStorage.setItem("INVOICE_COUNT", invoiceCount);
        localStorage.setItem("BRAND_LOGO", data.companyLogo);
        localStorage.setItem("AUTH_USER_DETAILS", JSON.stringify(data));
        localStorage.setItem("AUTH_2F", String(0));
        localStorage.setItem("isException", exceptionRoleId);
        localStorage.setItem("isAddSupplier", data.isAddSupplier);
        localStorage.setItem("LOGIN_NAME", data.firstName + " " + data.lastName);
        localStorage.setItem("API_TIME", new Date().toISOString());
        localStorage.setItem("PAGE_SIZE_PRESET", data.pageSizePreference || "standard");
        localStorage.setItem("INVOICE_NAV_SCOPE", data.invoiceNavScope || "all");

        // Dispatch Redux actions
        dispatch({
          type: UPDATE_USER_PROFILE,
          payload: data.profileLogo,
          userName: data.firstName + " " + data.lastName,
        });
        dispatch({
          type: UPDATE_COMPANY_LOGO,
          payload: data.companyLogo,
        });

        // Fetch team details (same as Signin.js post-login)
        try {
          const config = {
            method: "GET",
            url: GET_TEAMDETAILS_BY_ID,
            headers: authHeader(),
            params: { teamId: data.teamId },
          };
          const response = await API(config);
          const teamResult = response.data;

          if (teamResult.status === "Success") {
            localStorage.setItem(
              "Suppliermaintanceemail",
              teamResult.data[0].supplierMaintenanceEmail
            );
          }
        } catch (teamErr) {
          // Non-fatal: team details fetch failure shouldn't block login
          console.error("[SSOCallback] Team details fetch error:", teamErr);
        }

        // Refresh feature flags and permissions now that teamId is available in localStorage
        await refreshFlags();
        await refreshPermissions();

        // Navigate to dashboard
        navigation(DASHBOARD_ROUTE);
      } catch (err) {
        console.error("[SSOCallback] Processing error:", err);
        setError("Sign-in failed. Redirecting to login page...");
        setTimeout(() => navigation(SIGNIN_ROUTE), 3000);
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once: SSO callback processes URL params once on mount
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
      }}
    >
      {error ? (
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      ) : (
        <>
          <CircularProgress size={48} />
          <Typography variant="h6" color="textSecondary">
            Completing sign in...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default SSOCallback;
